// App.jsx

import { Routes, Route, Navigate } from "react-router-dom";
import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import LandingPage from "views/landing";
import Settings from "views/admin/main/account/settings";
import "assets/css/Plugins.css";
import { useState, useEffect } from "react";
import ProductNew from "views/admin/main/ecommerce/newProject";
import { AuthProvider } from "contexts/authContext";
import ProtectedRoute from "contexts/ProtectedRoute";
import OthersError from "views/admin/main/others/404";
import Access from "views/access";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChatWidgetLite from "widgets/ChatWidgetLite";

const App = () => {
  const [themeApp, setThemeApp] = useState({
    "--background-100": "#FFFFFF",
    "--background-900": "#070f2e",
    "--shadow-100": "rgba(112, 144, 176, 0.08)",
    "--color-50": "#E9E3FF",
    "--color-100": "#C0B8FE",
    "--color-200": "#A195FD",
    "--color-300": "#8171FC",
    "--color-400": "#7551FF",
    "--color-500": "#FF6000",
    "--color-600": "#3311DB",
    "--color-700": "#2111A5",
    "--color-800": "#190793",
    "--color-900": "#11047A",
  });

  const [mini, setMini] = useState(false);
  const queryClient = new QueryClient();

  // Theme variable injection
  useEffect(() => {
    for (const color in themeApp) {
      document.documentElement.style.setProperty(color, themeApp[color]);
    }
  }, [themeApp]);

  // Chat widget state
  const [chatOpen, setChatOpen] = useState(false);
  const [theme, setTheme] = useState("light");

  // Detect system theme
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setTheme(mediaQuery.matches ? "dark" : "light");
    const handler = (e) => setTheme(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // ESC key to close chat
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setChatOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <Routes>
          {/* Public Routes */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="auth/*" element={<AuthLayout />} />
          <Route path="*" element={<OthersError />} /> {/* 404 Page Route */}

          <Route
            path="/course"
            element={
              <ProtectedRoute
                element={<Access />}
                requiresSubscription={true}
                courseId={1}
              />
            }
          />

          {/* General Admin Routes */}
          <Route
            path="admin/*"
            element={
              <ProtectedRoute
                element={
                  <AdminLayout
                    setMini={setMini}
                    mini={mini}
                    theme={themeApp}
                    setTheme={setThemeApp}
                  />
                }
              />
            }
          >
            {/* Authenticated Sub-Routes */}
            <Route
              path="main/ecommerce/new-project"
              element={<ProductNew />}
            />
            <Route path="main/account/settings" element={<Settings />} />
          </Route>

          {/* Redirect root to admin dashboard */}
          <Route path="/" element={<Navigate to="/admin" replace />} />
        </Routes>

        {/* React Query Devtools */}
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}

        {/* Toast Container */}
        <ToastContainer
          position="top-center"
          autoClose={8000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            fontFamily: "DM Sans, sans-serif",
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }}
          progressStyle={{
            background: "linear-gradient(to right, #f97316, #ea580c)",
          }}
        />

        {/* Floating Chat Button */}
        <button
          type="button"
          aria-label="Open iForge Assistant"
          className="chat-fab"
          onClick={() => setChatOpen(true)}
          title="Chat with iForge Assistant"
        >
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="currentColor"
            style={{ display: "block", margin: "0 auto" }}
          >
            <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2ZM7 9h10v2H7V9Zm0-4h10v2H7V5Zm0 8h7v2H7v-2Z" />
          </svg>
        </button>

        {/* Chat Modal */}
        {chatOpen && (
          <>
            <div
              className="chat-overlay"
              onClick={() => setChatOpen(false)}
            />
            <div
              className="chat-modal"
              role="dialog"
              aria-modal="true"
              aria-label="iForge Assistant"
            >
              <div className="chat-modal__header">
                <div>iForge Assistant</div>
                <button
                  className="chat-modal__close"
                  onClick={() => setChatOpen(false)}
                  aria-label="Close chat"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="38"
                    height="18"
                    fill="currentColor"
                  >
                    <path d="M6.4 4.98 4.98 6.4 10.58 12l-5.6 5.6 1.42 1.42L12 13.42l5.6 5.6 1.42-1.42-5.6-5.6 5.6-5.6-1.42-1.42-5.6 5.6-5.6-5.6Z" />
                  </svg>
                </button>
              </div>
              <div className="chat-modal__body" style={{ height: "100%" }}>
                <ChatWidgetLite apiBase="https://iforgebot.codesbynewton.com" theme={theme} />
              </div>
            </div>
          </>
        )}
      </QueryClientProvider>
    </AuthProvider>
  );
};

export default App;
